import { defaultModules, QUILL_CONFIG_TOKEN } from 'ngx-quill/config';
const _c0 = [[["", "above-quill-editor-toolbar", ""]], [["", "quill-editor-toolbar", ""]], [["", "below-quill-editor-toolbar", ""]]];
const _c1 = ["[above-quill-editor-toolbar]", "[quill-editor-toolbar]", "[below-quill-editor-toolbar]"];
function QuillEditorComponent_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵdomElement(0, "div", 0);
  }
}
function QuillEditorComponent_Conditional_4_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵdomElement(0, "div", 0);
  }
}
export * from 'ngx-quill/config';
import * as i0 from '@angular/core';
import { inject, Injectable, input, EventEmitter, signal, ElementRef, ChangeDetectorRef, PLATFORM_ID, Renderer2, NgZone, DestroyRef, SecurityContext, Output, Directive, forwardRef, ViewEncapsulation, Component, NgModule } from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { DomSanitizer } from '@angular/platform-browser';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, defer, forkJoin, of, isObservable, fromEvent, Subscription } from 'rxjs';
import { shareReplay, map, tap, mergeMap, debounceTime } from 'rxjs/operators';
import { NG_VALUE_ACCESSOR, NG_VALIDATORS } from '@angular/forms';
const getFormat = (format, configFormat) => {
  const passedFormat = format || configFormat;
  return passedFormat || 'html';
};
const raf$ = () => {
  return new Observable(subscriber => {
    const rafId = requestAnimationFrame(() => {
      subscriber.next();
      subscriber.complete();
    });
    return () => cancelAnimationFrame(rafId);
  });
};
let QuillService = /*#__PURE__*/(() => {
  class QuillService {
    constructor() {
      this.config = inject(QUILL_CONFIG_TOKEN) || {
        modules: defaultModules
      };
      this.quill$ = defer(async () => {
        if (!this.Quill) {
          // Quill adds event listeners on import:
          // https://github.com/quilljs/quill/blob/develop/core/emitter.js#L8
          // We want to use the unpatched `addEventListener` method to ensure all event
          // callbacks run outside of zone.
          // Since we don't yet know whether `zone.js` is used, we simply save the value
          // to restore it later.
          // We can use global `document` because we execute it only in the browser.
          const maybePatchedAddEventListener = document.addEventListener;
          // There are two types of Angular applications:
          // 1) default (with zone.js)
          // 2) zoneless
          // Developers can avoid importing the `zone.js` package and inform Angular that
          // they are responsible for running change detection manually.
          // This can be done using `provideZonelessChangeDetection()`.
          // We fall back to `document.addEventListener` if `__zone_symbol__addEventListener`
          // is not defined, which indicates that `zone.js` is not imported.
          // The `__zone_symbol__addEventListener` is essentially the native DOM API,
          // unpatched by zone.js, meaning it does not go through the `zone.js` task lifecycle.
          document.addEventListener = document['__zone_symbol__addEventListener'] || document.addEventListener;
          const {
            Quill
          } = await import('./ngx-quill-quill-CUw8Q_m0.mjs');
          document.addEventListener = maybePatchedAddEventListener;
          this.Quill = Quill;
        }
        // Only register custom options and modules once
        this.config.customOptions?.forEach(customOption => {
          const newCustomOption = this.Quill.import(customOption.import);
          newCustomOption.whitelist = customOption.whitelist;
          this.Quill.register(newCustomOption, true, this.config.suppressGlobalRegisterWarning);
        });
        // Use `Promise` directly to avoid bundling `firstValueFrom`.
        return new Promise(resolve => {
          this.registerCustomModules(this.Quill, this.config.customModules, this.config.suppressGlobalRegisterWarning).subscribe(resolve);
        });
      }).pipe(shareReplay({
        bufferSize: 1,
        refCount: false
      }));
      // A list of custom modules that have already been registered,
      // so we don’t need to await their implementation.
      this.registeredModules = new Set();
    }
    getQuill() {
      return this.quill$;
    }
    /** @internal */
    beforeRender(Quill, customModules, beforeRender = this.config.beforeRender) {
      // This function is called each time the editor needs to be rendered,
      // so it operates individually per component. If no custom module needs to be
      // registered and no `beforeRender` function is provided, it will emit
      // immediately and proceed with the rendering.
      const sources = [this.registerCustomModules(Quill, customModules)];
      if (beforeRender) {
        sources.push(beforeRender());
      }
      return forkJoin(sources).pipe(map(() => Quill));
    }
    /** @internal */
    registerCustomModules(Quill, customModules, suppressGlobalRegisterWarning) {
      if (!Array.isArray(customModules)) {
        return of(Quill);
      }
      const sources = [];
      for (const customModule of customModules) {
        const {
          path,
          implementation: maybeImplementation
        } = customModule;
        // If the module is already registered, proceed to the next module...
        if (this.registeredModules.has(path)) {
          continue;
        }
        this.registeredModules.add(path);
        if (isObservable(maybeImplementation)) {
          // If the implementation is an observable, we will wait for it to load and
          // then register it with Quill. The caller will wait until the module is registered.
          sources.push(maybeImplementation.pipe(tap(implementation => {
            Quill.register(path, implementation, suppressGlobalRegisterWarning);
          })));
        } else {
          Quill.register(path, maybeImplementation, suppressGlobalRegisterWarning);
        }
      }
      return sources.length > 0 ? forkJoin(sources).pipe(map(() => Quill)) : of(Quill);
    }
    static {
      this.ɵfac = function QuillService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || QuillService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: QuillService,
        factory: QuillService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return QuillService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let QuillEditorBase = /*#__PURE__*/(() => {
  class QuillEditorBase {
    constructor() {
      this.format = input(undefined);
      this.theme = input(undefined);
      this.modules = input(undefined);
      this.debug = input(false);
      this.readOnly = input(false);
      this.placeholder = input(undefined);
      this.maxLength = input(undefined);
      this.minLength = input(undefined);
      this.required = input(false);
      this.formats = input(undefined);
      this.customToolbarPosition = input('top');
      this.sanitize = input(undefined);
      this.beforeRender = input(undefined);
      this.styles = input(null);
      this.registry = input(undefined);
      this.bounds = input(undefined);
      this.customOptions = input([]);
      this.customModules = input([]);
      this.trackChanges = input(undefined);
      this.classes = input(undefined);
      this.trimOnValidation = input(false);
      this.linkPlaceholder = input(undefined);
      this.compareValues = input(false);
      this.filterNull = input(false);
      this.debounceTime = input(undefined);
      /*
      https://github.com/KillerCodeMonkey/ngx-quill/issues/1257 - fix null value set
             provide default empty value
      by default null
             e.g. defaultEmptyValue="" - empty string
             <quill-editor
        defaultEmptyValue=""
        formControlName="message"
      ></quill-editor>
      */
      this.defaultEmptyValue = input(null);
      this.onEditorCreated = new EventEmitter();
      this.onEditorChanged = new EventEmitter();
      this.onContentChanged = new EventEmitter();
      this.onSelectionChanged = new EventEmitter();
      this.onFocus = new EventEmitter();
      this.onBlur = new EventEmitter();
      this.onNativeFocus = new EventEmitter();
      this.onNativeBlur = new EventEmitter();
      this.disabled = false; // used to store initial value before ViewInit
      this.toolbarPosition = signal('top');
      this.eventsSubscription = null;
      this.quillSubscription = null;
      this.elementRef = inject(ElementRef);
      this.cd = inject(ChangeDetectorRef);
      this.domSanitizer = inject(DomSanitizer);
      this.platformId = inject(PLATFORM_ID);
      this.renderer = inject(Renderer2);
      this.zone = inject(NgZone);
      this.service = inject(QuillService);
      this.destroyRef = inject(DestroyRef);
      this.valueGetter = input(quillEditor => {
        let html = quillEditor.getSemanticHTML();
        if (this.isEmptyValue(html)) {
          html = this.defaultEmptyValue();
        }
        let modelValue = html;
        const format = getFormat(this.format(), this.service.config.format);
        if (format === 'text') {
          modelValue = quillEditor.getText();
        } else if (format === 'object') {
          modelValue = quillEditor.getContents();
        } else if (format === 'json') {
          try {
            modelValue = JSON.stringify(quillEditor.getContents());
          } catch {
            modelValue = quillEditor.getText();
          }
        }
        return modelValue;
      });
      this.valueSetter = input((quillEditor, value) => {
        const format = getFormat(this.format(), this.service.config.format);
        if (format === 'html') {
          const sanitize = [true, false].includes(this.sanitize()) ? this.sanitize() : this.service.config.sanitize || false;
          if (sanitize) {
            value = this.domSanitizer.sanitize(SecurityContext.HTML, value);
          }
          return quillEditor.clipboard.convert({
            html: value
          });
        } else if (format === 'json') {
          try {
            return JSON.parse(value);
          } catch {
            return [{
              insert: value
            }];
          }
        }
        return value;
      });
      this.selectionChangeHandler = (range, oldRange, source) => {
        const trackChanges = this.trackChanges() || this.service.config.trackChanges;
        const shouldTriggerOnModelTouched = !range && !!this.onModelTouched && (source === 'user' || trackChanges && trackChanges === 'all');
        // only emit changes when there's any listener
        if (!this.onBlur.observed && !this.onFocus.observed && !this.onSelectionChanged.observed && !shouldTriggerOnModelTouched) {
          return;
        }
        this.zone.run(() => {
          if (range === null) {
            this.onBlur.emit({
              editor: this.quillEditor,
              source
            });
          } else if (oldRange === null) {
            this.onFocus.emit({
              editor: this.quillEditor,
              source
            });
          }
          this.onSelectionChanged.emit({
            editor: this.quillEditor,
            oldRange,
            range,
            source
          });
          if (shouldTriggerOnModelTouched) {
            this.onModelTouched();
          }
          this.cd.markForCheck();
        });
      };
      this.textChangeHandler = (delta, oldDelta, source) => {
        // only emit changes emitted by user interactions
        const text = this.quillEditor.getText();
        const content = this.quillEditor.getContents();
        let html = this.quillEditor.getSemanticHTML();
        if (this.isEmptyValue(html)) {
          html = this.defaultEmptyValue();
        }
        const trackChanges = this.trackChanges() || this.service.config.trackChanges;
        const shouldTriggerOnModelChange = (source === 'user' || trackChanges && trackChanges === 'all') && !!this.onModelChange;
        // only emit changes when there's any listener
        if (!this.onContentChanged.observed && !shouldTriggerOnModelChange) {
          return;
        }
        this.zone.run(() => {
          if (shouldTriggerOnModelChange) {
            const valueGetter = this.valueGetter();
            this.onModelChange(valueGetter(this.quillEditor));
          }
          this.onContentChanged.emit({
            content,
            delta,
            editor: this.quillEditor,
            html,
            oldDelta,
            source,
            text
          });
          this.cd.markForCheck();
        });
      };
      this.editorChangeHandler = (event, current, old, source) => {
        // only emit changes when there's any listener
        if (!this.onEditorChanged.observed) {
          return;
        }
        // only emit changes emitted by user interactions
        if (event === 'text-change') {
          const text = this.quillEditor.getText();
          const content = this.quillEditor.getContents();
          let html = this.quillEditor.getSemanticHTML();
          if (this.isEmptyValue(html)) {
            html = this.defaultEmptyValue();
          }
          this.zone.run(() => {
            this.onEditorChanged.emit({
              content,
              delta: current,
              editor: this.quillEditor,
              event,
              html,
              oldDelta: old,
              source,
              text
            });
            this.cd.markForCheck();
          });
        } else {
          this.zone.run(() => {
            this.onEditorChanged.emit({
              editor: this.quillEditor,
              event,
              oldRange: old,
              range: current,
              source
            });
            this.cd.markForCheck();
          });
        }
      };
      this.destroyRef.onDestroy(() => {
        this.dispose();
        this.quillSubscription?.unsubscribe();
        this.quillSubscription = null;
      });
    }
    static normalizeClassNames(classes) {
      const classList = classes.trim().split(' ');
      return classList.reduce((prev, cur) => {
        const trimmed = cur.trim();
        if (trimmed) {
          prev.push(trimmed);
        }
        return prev;
      }, []);
    }
    ngOnInit() {
      this.toolbarPosition.set(this.customToolbarPosition());
    }
    ngAfterViewInit() {
      if (isPlatformServer(this.platformId)) {
        return;
      }
      // The `quill-editor` component might be destroyed before the `quill` chunk is loaded and its code is executed
      // this will lead to runtime exceptions, since the code will be executed on DOM nodes that don't exist within the tree.
      this.quillSubscription = this.service.getQuill().pipe(mergeMap(Quill => this.service.beforeRender(Quill, this.customModules(), this.beforeRender()))).subscribe(Quill => {
        this.editorElem = this.elementRef.nativeElement.querySelector('[quill-editor-element]');
        const toolbarElem = this.elementRef.nativeElement.querySelector('[quill-editor-toolbar]');
        const modules = Object.assign({}, this.modules() || this.service.config.modules);
        if (toolbarElem) {
          modules.toolbar = toolbarElem;
        } else if (modules.toolbar === undefined) {
          modules.toolbar = defaultModules.toolbar;
        }
        let placeholder = this.placeholder() !== undefined ? this.placeholder() : this.service.config.placeholder;
        if (placeholder === undefined) {
          placeholder = 'Insert text here ...';
        }
        const styles = this.styles();
        if (styles) {
          Object.keys(styles).forEach(key => {
            this.renderer.setStyle(this.editorElem, key, styles[key]);
          });
        }
        if (this.classes()) {
          this.addClasses(this.classes());
        }
        this.customOptions().forEach(customOption => {
          const newCustomOption = Quill.import(customOption.import);
          newCustomOption.whitelist = customOption.whitelist;
          Quill.register(newCustomOption, true);
        });
        let bounds = this.bounds() && this.bounds() === 'self' ? this.editorElem : this.bounds();
        if (!bounds) {
          // Can use global `document` because we execute this only in the browser.
          bounds = this.service.config.bounds ? this.service.config.bounds : document.body;
        }
        let debug = this.debug();
        if (!debug && debug !== false && this.service.config.debug) {
          debug = this.service.config.debug;
        }
        let readOnly = this.readOnly();
        if (!readOnly && this.readOnly() !== false) {
          readOnly = this.service.config.readOnly !== undefined ? this.service.config.readOnly : false;
        }
        let formats = this.formats();
        if (!formats && formats === undefined) {
          formats = this.service.config.formats ? [...this.service.config.formats] : this.service.config.formats === null ? null : undefined;
        }
        this.zone.runOutsideAngular(() => {
          this.quillEditor = new Quill(this.editorElem, {
            bounds,
            debug,
            formats,
            modules,
            placeholder,
            readOnly,
            registry: this.registry(),
            theme: this.theme() || (this.service.config.theme ? this.service.config.theme : 'snow')
          });
          if (this.onNativeBlur.observed) {
            // https://github.com/quilljs/quill/issues/2186#issuecomment-533401328
            fromEvent(this.quillEditor.scroll.domNode, 'blur').pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.onNativeBlur.next({
              editor: this.quillEditor,
              source: 'dom'
            }));
            // https://github.com/quilljs/quill/issues/2186#issuecomment-803257538
            const toolbar = this.quillEditor.getModule('toolbar');
            if (toolbar.container) {
              fromEvent(toolbar.container, 'mousedown').pipe(takeUntilDestroyed(this.destroyRef)).subscribe(e => e.preventDefault());
            }
          }
          if (this.onNativeFocus.observed) {
            fromEvent(this.quillEditor.scroll.domNode, 'focus').pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.onNativeFocus.next({
              editor: this.quillEditor,
              source: 'dom'
            }));
          }
          // Set optional link placeholder, Quill has no native API for it so using workaround
          if (this.linkPlaceholder()) {
            const tooltip = this.quillEditor?.theme?.tooltip;
            const input = tooltip?.root?.querySelector('input[data-link]');
            if (input?.dataset) {
              input.dataset.link = this.linkPlaceholder();
            }
          }
        });
        if (this.content) {
          const format = getFormat(this.format(), this.service.config.format);
          if (format === 'text') {
            this.quillEditor.setText(this.content, 'silent');
          } else {
            const valueSetter = this.valueSetter();
            const newValue = valueSetter(this.quillEditor, this.content);
            this.quillEditor.setContents(newValue, 'silent');
          }
          const history = this.quillEditor.getModule('history');
          history.clear();
        }
        // initialize disabled status based on this.disabled as default value
        this.setDisabledState();
        this.addQuillEventListeners();
        // The `requestAnimationFrame` triggers change detection. There's no sense to invoke the `requestAnimationFrame` if anyone is
        // listening to the `onEditorCreated` event inside the template, for instance `<quill-view (onEditorCreated)="...">`.
        if (!this.onEditorCreated.observed && !this.onValidatorChanged) {
          return;
        }
        // The `requestAnimationFrame` will trigger change detection and `onEditorCreated` will also call `markDirty()`
        // internally, since Angular wraps template event listeners into `listener` instruction. We're using the `requestAnimationFrame`
        // to prevent the frame drop and avoid `ExpressionChangedAfterItHasBeenCheckedError` error.
        raf$().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
          if (this.onValidatorChanged) {
            this.onValidatorChanged();
          }
          this.onEditorCreated.emit(this.quillEditor);
        });
      });
    }
    ngOnChanges(changes) {
      if (!this.quillEditor) {
        return;
      }
      if (changes.readOnly) {
        this.quillEditor.enable(!changes.readOnly.currentValue);
      }
      if (changes.placeholder) {
        this.quillEditor.root.dataset.placeholder = changes.placeholder.currentValue;
      }
      if (changes.styles) {
        const currentStyling = changes.styles.currentValue;
        const previousStyling = changes.styles.previousValue;
        if (previousStyling) {
          Object.keys(previousStyling).forEach(key => {
            this.renderer.removeStyle(this.editorElem, key);
          });
        }
        if (currentStyling) {
          Object.keys(currentStyling).forEach(key => {
            this.renderer.setStyle(this.editorElem, key, this.styles()[key]);
          });
        }
      }
      if (changes.classes) {
        const currentClasses = changes.classes.currentValue;
        const previousClasses = changes.classes.previousValue;
        if (previousClasses) {
          this.removeClasses(previousClasses);
        }
        if (currentClasses) {
          this.addClasses(currentClasses);
        }
      }
      // We'd want to re-apply event listeners if the `debounceTime` binding changes to apply the
      // `debounceTime` operator or vice-versa remove it.
      if (changes.debounceTime) {
        this.addQuillEventListeners();
      }
    }
    addClasses(classList) {
      QuillEditorBase.normalizeClassNames(classList).forEach(c => {
        this.renderer.addClass(this.editorElem, c);
      });
    }
    removeClasses(classList) {
      QuillEditorBase.normalizeClassNames(classList).forEach(c => {
        this.renderer.removeClass(this.editorElem, c);
      });
    }
    writeValue(currentValue) {
      // optional fix for https://github.com/angular/angular/issues/14988
      if (this.filterNull() && currentValue === null) {
        return;
      }
      this.content = currentValue;
      if (!this.quillEditor) {
        return;
      }
      const format = getFormat(this.format(), this.service.config.format);
      const valueSetter = this.valueSetter();
      const newValue = valueSetter(this.quillEditor, currentValue);
      if (this.compareValues()) {
        const currentEditorValue = this.quillEditor.getContents();
        if (JSON.stringify(currentEditorValue) === JSON.stringify(newValue)) {
          return;
        }
      }
      if (currentValue) {
        if (format === 'text') {
          this.quillEditor.setText(currentValue);
        } else {
          this.quillEditor.setContents(newValue);
        }
        return;
      }
      this.quillEditor.setText('');
    }
    setDisabledState(isDisabled = this.disabled) {
      // store initial value to set appropriate disabled status after ViewInit
      this.disabled = isDisabled;
      if (this.quillEditor) {
        if (isDisabled) {
          this.quillEditor.disable();
          this.renderer.setAttribute(this.elementRef.nativeElement, 'disabled', 'disabled');
        } else {
          if (!this.readOnly()) {
            this.quillEditor.enable();
          }
          this.renderer.removeAttribute(this.elementRef.nativeElement, 'disabled');
        }
      }
    }
    registerOnChange(fn) {
      this.onModelChange = fn;
    }
    registerOnTouched(fn) {
      this.onModelTouched = fn;
    }
    registerOnValidatorChange(fn) {
      this.onValidatorChanged = fn;
    }
    validate() {
      if (!this.quillEditor) {
        return null;
      }
      const err = {};
      let valid = true;
      const text = this.quillEditor.getText();
      // trim text if wanted + handle special case that an empty editor contains a new line
      const textLength = this.trimOnValidation() ? text.trim().length : text.length === 1 && text.trim().length === 0 ? 0 : text.length - 1;
      const deltaOperations = this.quillEditor.getContents().ops;
      const onlyEmptyOperation = !!deltaOperations && deltaOperations.length === 1 && ['\n', ''].includes(deltaOperations[0].insert?.toString());
      if (this.minLength() && textLength && textLength < this.minLength()) {
        err.minLengthError = {
          given: textLength,
          minLength: this.minLength()
        };
        valid = false;
      }
      if (this.maxLength() && textLength > this.maxLength()) {
        err.maxLengthError = {
          given: textLength,
          maxLength: this.maxLength()
        };
        valid = false;
      }
      if (this.required() && !textLength && onlyEmptyOperation) {
        err.requiredError = {
          empty: true
        };
        valid = false;
      }
      return valid ? null : err;
    }
    addQuillEventListeners() {
      this.dispose();
      // We have to enter the `<root>` zone when adding event listeners, so `debounceTime` will spawn the
      // `AsyncAction` there w/o triggering change detections. We still re-enter the Angular's zone through
      // `zone.run` when we emit an event to the parent component.
      this.zone.runOutsideAngular(() => {
        this.eventsSubscription = new Subscription();
        this.eventsSubscription.add(
        // mark model as touched if editor lost focus
        fromEvent(this.quillEditor, 'selection-change').subscribe(([range, oldRange, source]) => {
          this.selectionChangeHandler(range, oldRange, source);
        }));
        // The `fromEvent` supports passing JQuery-style event targets, the editor has `on` and `off` methods which
        // will be invoked upon subscription and teardown.
        let textChange$ = fromEvent(this.quillEditor, 'text-change');
        let editorChange$ = fromEvent(this.quillEditor, 'editor-change');
        if (typeof this.debounceTime() === 'number') {
          textChange$ = textChange$.pipe(debounceTime(this.debounceTime()));
          editorChange$ = editorChange$.pipe(debounceTime(this.debounceTime()));
        }
        this.eventsSubscription.add(
        // update model if text changes
        textChange$.subscribe(([delta, oldDelta, source]) => {
          this.textChangeHandler(delta, oldDelta, source);
        }));
        this.eventsSubscription.add(
        // triggered if selection or text changed
        editorChange$.subscribe(([event, current, old, source]) => {
          this.editorChangeHandler(event, current, old, source);
        }));
      });
    }
    dispose() {
      this.eventsSubscription?.unsubscribe();
      this.eventsSubscription = null;
    }
    isEmptyValue(html) {
      return html === '<p></p>' || html === '<div></div>' || html === '<p><br></p>' || html === '<div><br></div>';
    }
    static {
      this.ɵfac = function QuillEditorBase_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || QuillEditorBase)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: QuillEditorBase,
        inputs: {
          format: [1, "format"],
          theme: [1, "theme"],
          modules: [1, "modules"],
          debug: [1, "debug"],
          readOnly: [1, "readOnly"],
          placeholder: [1, "placeholder"],
          maxLength: [1, "maxLength"],
          minLength: [1, "minLength"],
          required: [1, "required"],
          formats: [1, "formats"],
          customToolbarPosition: [1, "customToolbarPosition"],
          sanitize: [1, "sanitize"],
          beforeRender: [1, "beforeRender"],
          styles: [1, "styles"],
          registry: [1, "registry"],
          bounds: [1, "bounds"],
          customOptions: [1, "customOptions"],
          customModules: [1, "customModules"],
          trackChanges: [1, "trackChanges"],
          classes: [1, "classes"],
          trimOnValidation: [1, "trimOnValidation"],
          linkPlaceholder: [1, "linkPlaceholder"],
          compareValues: [1, "compareValues"],
          filterNull: [1, "filterNull"],
          debounceTime: [1, "debounceTime"],
          defaultEmptyValue: [1, "defaultEmptyValue"],
          valueGetter: [1, "valueGetter"],
          valueSetter: [1, "valueSetter"]
        },
        outputs: {
          onEditorCreated: "onEditorCreated",
          onEditorChanged: "onEditorChanged",
          onContentChanged: "onContentChanged",
          onSelectionChanged: "onSelectionChanged",
          onFocus: "onFocus",
          onBlur: "onBlur",
          onNativeFocus: "onNativeFocus",
          onNativeBlur: "onNativeBlur"
        },
        features: [i0.ɵɵNgOnChangesFeature]
      });
    }
  }
  return QuillEditorBase;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let QuillEditorComponent = /*#__PURE__*/(() => {
  class QuillEditorComponent extends QuillEditorBase {
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵQuillEditorComponent_BaseFactory;
        return function QuillEditorComponent_Factory(__ngFactoryType__) {
          return (ɵQuillEditorComponent_BaseFactory || (ɵQuillEditorComponent_BaseFactory = i0.ɵɵgetInheritedFactory(QuillEditorComponent)))(__ngFactoryType__ || QuillEditorComponent);
        };
      })();
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: QuillEditorComponent,
        selectors: [["quill-editor"]],
        features: [i0.ɵɵProvidersFeature([{
          multi: true,
          provide: NG_VALUE_ACCESSOR,
          useExisting: forwardRef(() => QuillEditorComponent)
        }, {
          multi: true,
          provide: NG_VALIDATORS,
          useExisting: forwardRef(() => QuillEditorComponent)
        }]), i0.ɵɵInheritDefinitionFeature],
        ngContentSelectors: _c1,
        decls: 5,
        vars: 2,
        consts: [["quill-editor-element", ""]],
        template: function QuillEditorComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵprojectionDef(_c0);
            i0.ɵɵconditionalCreate(0, QuillEditorComponent_Conditional_0_Template, 1, 0, "div", 0);
            i0.ɵɵprojection(1);
            i0.ɵɵprojection(2, 1);
            i0.ɵɵprojection(3, 2);
            i0.ɵɵconditionalCreate(4, QuillEditorComponent_Conditional_4_Template, 1, 0, "div", 0);
          }
          if (rf & 2) {
            i0.ɵɵconditional(ctx.toolbarPosition() !== "top" ? 0 : -1);
            i0.ɵɵadvance(4);
            i0.ɵɵconditional(ctx.toolbarPosition() === "top" ? 4 : -1);
          }
        },
        styles: ["[_nghost-%COMP%]{display:inline-block}"]
      });
    }
  }
  return QuillEditorComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let QuillViewHTMLComponent = /*#__PURE__*/(() => {
  class QuillViewHTMLComponent {
    constructor() {
      this.content = input('');
      this.theme = input(undefined);
      this.sanitize = input(undefined);
      this.innerHTML = signal('');
      this.themeClass = signal('ql-snow');
      this.sanitizer = inject(DomSanitizer);
      this.service = inject(QuillService);
    }
    ngOnChanges(changes) {
      if (changes.theme) {
        const theme = changes.theme.currentValue || (this.service.config.theme ? this.service.config.theme : 'snow');
        this.themeClass.set(`ql-${theme} ngx-quill-view-html`);
      } else if (!this.theme()) {
        const theme = this.service.config.theme ? this.service.config.theme : 'snow';
        this.themeClass.set(`ql-${theme} ngx-quill-view-html`);
      }
      if (changes.content) {
        const content = changes.content.currentValue;
        const sanitize = [true, false].includes(this.sanitize()) ? this.sanitize() : this.service.config.sanitize || false;
        const innerHTML = sanitize ? content : this.sanitizer.bypassSecurityTrustHtml(content);
        this.innerHTML.set(innerHTML);
      }
    }
    static {
      this.ɵfac = function QuillViewHTMLComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || QuillViewHTMLComponent)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: QuillViewHTMLComponent,
        selectors: [["quill-view-html"]],
        inputs: {
          content: [1, "content"],
          theme: [1, "theme"],
          sanitize: [1, "sanitize"]
        },
        features: [i0.ɵɵNgOnChangesFeature],
        decls: 2,
        vars: 3,
        consts: [[1, "ql-container"], [1, "ql-editor", 3, "innerHTML"]],
        template: function QuillViewHTMLComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵdomElementStart(0, "div", 0);
            i0.ɵɵdomElement(1, "div", 1);
            i0.ɵɵdomElementEnd();
          }
          if (rf & 2) {
            i0.ɵɵclassMap(ctx.themeClass());
            i0.ɵɵadvance();
            i0.ɵɵdomProperty("innerHTML", ctx.innerHTML(), i0.ɵɵsanitizeHtml);
          }
        },
        styles: [".ql-container.ngx-quill-view-html{border:0}\n"],
        encapsulation: 2
      });
    }
  }
  return QuillViewHTMLComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let QuillViewComponent = /*#__PURE__*/(() => {
  class QuillViewComponent {
    constructor() {
      this.format = input(undefined);
      this.theme = input(undefined);
      this.modules = input(undefined);
      this.debug = input(false);
      this.formats = input(undefined);
      this.sanitize = input(undefined);
      this.beforeRender = input();
      this.strict = input(true);
      this.content = input();
      this.customModules = input([]);
      this.customOptions = input([]);
      this.onEditorCreated = new EventEmitter();
      this.elementRef = inject(ElementRef);
      this.renderer = inject(Renderer2);
      this.ngZone = inject(NgZone);
      this.service = inject(QuillService);
      this.sanitizer = inject(DomSanitizer);
      this.platformId = inject(PLATFORM_ID);
      this.destroyRef = inject(DestroyRef);
      this.valueSetter = (quillEditor, value) => {
        const format = getFormat(this.format(), this.service.config.format);
        let content = value;
        if (format === 'text') {
          quillEditor.setText(content);
        } else {
          if (format === 'html') {
            const sanitize = [true, false].includes(this.sanitize()) ? this.sanitize() : this.service.config.sanitize || false;
            if (sanitize) {
              value = this.sanitizer.sanitize(SecurityContext.HTML, value);
            }
            content = quillEditor.clipboard.convert({
              html: value
            });
          } else if (format === 'json') {
            try {
              content = JSON.parse(value);
            } catch {
              content = [{
                insert: value
              }];
            }
          }
          quillEditor.setContents(content);
        }
      };
    }
    ngOnChanges(changes) {
      if (!this.quillEditor) {
        return;
      }
      if (changes.content) {
        this.valueSetter(this.quillEditor, changes.content.currentValue);
      }
    }
    ngAfterViewInit() {
      if (isPlatformServer(this.platformId)) {
        return;
      }
      const quillSubscription = this.service.getQuill().pipe(mergeMap(Quill => this.service.beforeRender(Quill, this.customModules(), this.beforeRender()))).subscribe(Quill => {
        const modules = Object.assign({}, this.modules() || this.service.config.modules);
        modules.toolbar = false;
        this.customOptions().forEach(customOption => {
          const newCustomOption = Quill.import(customOption.import);
          newCustomOption.whitelist = customOption.whitelist;
          Quill.register(newCustomOption, true);
        });
        let debug = this.debug();
        if (!debug && debug !== false && this.service.config.debug) {
          debug = this.service.config.debug;
        }
        let formats = this.formats();
        if (formats === undefined) {
          formats = this.service.config.formats ? [...this.service.config.formats] : this.service.config.formats === null ? null : undefined;
        }
        const theme = this.theme() || (this.service.config.theme ? this.service.config.theme : 'snow');
        this.editorElem = this.elementRef.nativeElement.querySelector('[quill-view-element]');
        this.ngZone.runOutsideAngular(() => {
          this.quillEditor = new Quill(this.editorElem, {
            debug,
            formats,
            modules,
            readOnly: true,
            strict: this.strict(),
            theme
          });
        });
        this.renderer.addClass(this.editorElem, 'ngx-quill-view');
        if (this.content()) {
          this.valueSetter(this.quillEditor, this.content());
        }
        // The `requestAnimationFrame` triggers change detection. There's no sense to invoke the `requestAnimationFrame` if anyone is
        // listening to the `onEditorCreated` event inside the template, for instance `<quill-view (onEditorCreated)="...">`.
        if (!this.onEditorCreated.observed) {
          return;
        }
        // The `requestAnimationFrame` will trigger change detection and `onEditorCreated` will also call `markDirty()`
        // internally, since Angular wraps template event listeners into `listener` instruction. We're using the `requestAnimationFrame`
        // to prevent the frame drop and avoid `ExpressionChangedAfterItHasBeenCheckedError` error.
        raf$().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
          this.onEditorCreated.emit(this.quillEditor);
        });
      });
      this.destroyRef.onDestroy(() => quillSubscription.unsubscribe());
    }
    static {
      this.ɵfac = function QuillViewComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || QuillViewComponent)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: QuillViewComponent,
        selectors: [["quill-view"]],
        inputs: {
          format: [1, "format"],
          theme: [1, "theme"],
          modules: [1, "modules"],
          debug: [1, "debug"],
          formats: [1, "formats"],
          sanitize: [1, "sanitize"],
          beforeRender: [1, "beforeRender"],
          strict: [1, "strict"],
          content: [1, "content"],
          customModules: [1, "customModules"],
          customOptions: [1, "customOptions"]
        },
        outputs: {
          onEditorCreated: "onEditorCreated"
        },
        features: [i0.ɵɵNgOnChangesFeature],
        decls: 1,
        vars: 0,
        consts: [["quill-view-element", ""]],
        template: function QuillViewComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵdomElement(0, "div", 0);
          }
        },
        styles: [".ql-container.ngx-quill-view{border:0}\n"],
        encapsulation: 2
      });
    }
  }
  return QuillViewComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let QuillModule = /*#__PURE__*/(() => {
  class QuillModule {
    static forRoot(config) {
      return {
        ngModule: QuillModule,
        providers: [{
          provide: QUILL_CONFIG_TOKEN,
          useValue: config
        }]
      };
    }
    static {
      this.ɵfac = function QuillModule_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || QuillModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: QuillModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({});
    }
  }
  return QuillModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * Public API Surface of ngx-quill
 */
// Re-export everything from the secondary entry-point so we can be backwards-compatible
// and don't introduce breaking changes for consumers.

/**
 * Generated bundle index. Do not edit.
 */

export { QuillEditorBase, QuillEditorComponent, QuillModule, QuillService, QuillViewComponent, QuillViewHTMLComponent };
